// components/workflow/SetupFields.js
//
// Editor for the workflow setup vars (Config-tab defaults + sandbox overrides).
// Operates on a setup object via value/onChange. `familyId` is the flow's
// mandatory family/subfamily: products and promos here are CONSTRAINED to that
// family's subtree. Products may be sellable measures OR sub-families, and
// MULTIPLE may be selected (unlike the flow-level family).
import React, { useState, useEffect, useMemo } from "react";
import API from "../../api";

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Sel({ label, val, opts, onChange }) {
  return (
    <Labeled label={label}>
      <select className="wf-input" value={val || ""} onChange={(e) => onChange(e.target.value || null)}>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Labeled>
  );
}

// Find a node by id anywhere in the tree.
function findNode(nodes, id) {
  for (const n of nodes || []) {
    if (String(n._id) === String(id)) return n;
    const hit = findNode(n.children, id);
    if (hit) return hit;
  }
  return null;
}

// Collect all ids in a subtree (the node itself + descendants).
function subtreeIds(node, acc = new Set()) {
  if (!node) return acc;
  acc.add(String(node._id));
  for (const k of node.children || []) subtreeIds(k, acc);
  return acc;
}

// Multi-select picker over the flow's family subtrees (union of all flow
// families). Sellable leaves + sub-families both selectable.
function ProductsPicker({ familyIds, selected, onChange }) {
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const ids = Array.isArray(familyIds) ? familyIds.filter(Boolean) : familyIds ? [familyIds] : [];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await API.get("/product-families/tree");
        setTree(res.data?.data || []);
      } catch {
        /* non-fatal */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Roots to render: the subtrees of EACH flow family (union). Each flow-family
  // node is shown as a labeled group so multiple same-named nodes (Rollo) differ.
  const roots = useMemo(() => {
    if (!ids.length) return tree;
    return ids.map((fid) => findNode(tree, fid)).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, JSON.stringify(ids)]);

  const sel = Array.isArray(selected) ? selected : [];
  const isOn = (id) => sel.some((p) => String(p.id) === String(id));
  const toggle = (node) => {
    const id = String(node._id);
    if (isOn(id)) {
      onChange(sel.filter((p) => String(p.id) !== id));
    } else {
      onChange([...sel, { kind: node.sellable ? "product" : "family", id, name: node.name }]);
    }
  };
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const renderNode = (node, depth) => {
    const kids = node.children || [];
    const open = expanded[node._id] ?? depth < 1;
    return (
      <div key={node._id}>
        <div className="flex items-center gap-1" style={{ paddingLeft: depth * 12 }}>
          {kids.length > 0 ? (
            <button type="button" onClick={() => toggleExpand(node._id)} className="text-gray-400 w-4 text-xs">
              {open ? "▾" : "▸"}
            </button>
          ) : (
            <span className="w-4 inline-block" />
          )}
          <label className="flex items-center gap-1.5 text-xs text-gray-200 cursor-pointer">
            <input type="checkbox" checked={isOn(node._id)} onChange={() => toggle(node)} />
            {node.name}
            {node.sellable ? <span className="text-[10px] text-emerald-400">·medida</span> : <span className="text-[10px] text-gray-500">·grupo</span>}
          </label>
        </div>
        {open && kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  };

  return (
    <div>
      <div className="text-xs text-gray-400 mb-1">
        {sel.length ? `${sel.length} seleccionado(s): ${sel.map((p) => p.name).filter(Boolean).join(", ")}` : "— ninguno —"}
        {sel.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="text-red-400 ml-2">
            limpiar
          </button>
        )}
      </div>
      <div className="border border-gray-700 rounded-lg p-2 max-h-56 overflow-y-auto bg-gray-900">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
            <span className="inline-block w-3.5 h-3.5 border-2 border-gray-600 border-t-emerald-400 rounded-full animate-spin" />
            Cargando productos…
          </div>
        ) : !ids.length ? (
          <p className="text-xs text-amber-400/80">Asigna primero una o más familias al flujo (pestaña Config).</p>
        ) : roots.length === 0 ? (
          <p className="text-xs text-gray-500">Las familias del flujo no tienen elementos.</p>
        ) : (
          roots.map((r) => renderNode(r, 0))
        )}
      </div>
    </div>
  );
}

// Promo picker constrained to promos within the flow's family subtrees (union).
function PromoPicker({ familyIds, value, onChange }) {
  const [promos, setPromos] = useState([]);
  const [tree, setTree] = useState([]);
  const ids = Array.isArray(familyIds) ? familyIds.filter(Boolean) : familyIds ? [familyIds] : [];

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([API.get("/promos"), API.get("/product-families/tree")]);
        setPromos(p.data?.data || p.data || []);
        setTree(t.data?.data || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const familySet = useMemo(() => {
    if (!ids.length) return null;
    const set = new Set();
    ids.forEach((fid) => {
      const node = findNode(tree, fid);
      if (node) subtreeIds(node, set);
    });
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, JSON.stringify(ids)]);

  const eligible = useMemo(() => {
    if (!familySet) return promos; // no family → don't filter
    const promoIds = (pr) => {
      const ids = [];
      // Real Promo model: promoProductIds[] (ProductFamily refs, populated to
      // objects by the route) + promoPrices[].productId.
      (pr.promoProductIds || []).forEach((x) => ids.push(String(x?._id || x)));
      (pr.promoPrices || []).forEach((x) => x?.productId && ids.push(String(x.productId?._id || x.productId)));
      return ids;
    };
    return promos.filter((pr) => promoIds(pr).some((id) => familySet.has(id)));
  }, [promos, familySet]);

  return (
    <select className="wf-input" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">— sin promo —</option>
      {eligible.map((p) => (
        <option key={p._id} value={p._id}>
          {p.name}
          {p.active === false ? " (inactiva)" : ""}
        </option>
      ))}
    </select>
  );
}

// Marketplace alterno: route a SUBSET of the already-selected products to a non-
// default point of sale (e.g. the Hanlob store). Price stays live from the default
// channel; only the buy link (and refund/delivery, if the POS defines them) come
// from the alt store. Everything off this list stays on the default marketplace.
function AltMarketplacePicker({ products, value, onChange }) {
  const [pos, setPos] = useState([]);
  const sel = Array.isArray(products) ? products : [];
  const posId = value?.posId || "";
  const routed = new Set((value?.productIds || []).map(String));

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get("/points-of-sale", { params: { active: true } });
        setPos(res.data?.data || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const setPosId = (id) => {
    if (!id) return onChange(null); // clear the whole alt-marketplace routing
    // Keep only routed ids that are still selected above.
    const keep = [...routed].filter((r) => sel.some((p) => String(p.id) === r));
    onChange({ posId: id, productIds: keep });
  };
  const toggle = (id) => {
    if (!posId) return;
    const next = new Set(routed);
    next.has(String(id)) ? next.delete(String(id)) : next.add(String(id));
    onChange({ posId, productIds: [...next] });
  };

  return (
    <div className="space-y-2">
      <Sel
        label="Marketplace alterno (opcional)"
        val={posId}
        onChange={setPosId}
        opts={[
          { value: "", label: "— usar el marketplace por defecto —" },
          ...pos.map((p) => ({ value: p._id, label: `${p.name}${p.kind && p.kind !== "mercadolibre" ? ` · ${p.kind}` : ""}` })),
        ]}
      />
      {posId && (
        <div className="rounded-lg border border-gray-700/50 bg-gray-900/30 p-3">
          <p className="text-xs text-gray-400 mb-2">
            ¿Cuáles de los productos seleccionados arriba se venden por este marketplace? El resto usa el canal por defecto.
          </p>
          {sel.length === 0 ? (
            <p className="text-xs text-amber-400">Selecciona productos arriba primero.</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {sel.map((p) => (
                <label key={String(p.id)} className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                  <input type="checkbox" checked={routed.has(String(p.id))} onChange={() => toggle(p.id)} />
                  <span>{p.name || String(p.id)}</span>
                </label>
              ))}
            </div>
          )}
          {routed.size > 0 && (
            <p className="text-[11px] text-sky-400 mt-2">
              {routed.size} producto(s) → este marketplace · precio en vivo del canal por defecto, link de este marketplace.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// hideAdvanced: drop the buyer/tone/purchase/channel selects (used by the sandbox,
// which only needs promo + product selection). Default shows all fields.
export default function SetupFields({ value = {}, onChange, familyIds = null, hideAdvanced = false }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="grid grid-cols-2 gap-3">
      {!hideAdvanced && (
        <>
          <Sel label="Cliente" val={v.buyer} onChange={(x) => set({ buyer: x })}
            opts={[{ value: "", label: "— (sin definir)" }, { value: "end_user", label: "Consumidor final" }, { value: "reseller", label: "Revendedor" }]} />
          <Sel label="Tono" val={v.tone} onChange={(x) => set({ tone: x })}
            opts={[{ value: "", label: "— (sin definir)" }, { value: "casual", label: "Casual" }, { value: "professional", label: "Profesional" }, { value: "technical", label: "Técnico" }]} />
          <Sel label="Tipo de compra" val={v.purchaseType} onChange={(x) => set({ purchaseType: x })}
            opts={[{ value: "", label: "— (sin definir)" }, { value: "retail", label: "Menudeo" }, { value: "wholesale", label: "Mayoreo" }]} />
          <Sel label="Canal de venta" val={v.saleChannel} onChange={(x) => set({ saleChannel: x })}
            opts={[{ value: "", label: "— (sin definir)" }, { value: "marketplace", label: "Marketplace" }, { value: "manual", label: "Manual (asesor)" }]} />
        </>
      )}
      <div className="col-span-2">
        <Labeled label="Productos (dentro de la familia del flujo — varios permitidos)">
          <ProductsPicker familyIds={familyIds} selected={v.products} onChange={(arr) => set({ products: arr })} />
        </Labeled>
      </div>
      <Labeled label="Promoción (dentro de la familia)">
        <PromoPicker familyIds={familyIds} value={v.hasPromo} onChange={(x) => set({ hasPromo: x })} />
      </Labeled>
      {!hideAdvanced && (
        <div className="col-span-2">
          <AltMarketplacePicker
            products={v.products}
            value={v.altMarketplace}
            onChange={(am) => set({ altMarketplace: am })}
          />
        </div>
      )}
      {/* Catalog and store link are NOT set per-ad (recipe for divergence).
          The store link comes from the company's available marketplaces, and
          the catalog from the product tree (climbing up to the general
          company catalog). Both resolve automatically at runtime. */}
    </div>
  );
}
